import { collectionMethods } from './collectionMethods.js';

const BASE_PATH = '/auction-houses';

/**
 * Auction Houses — the venues sales run at.
 *
 * The widest surface of the five. A house belongs to an Operation, may hold its
 * own legal identity and payer, registers the Dealerships allowed to buy there,
 * and bars people at its own tier — beyond the Operation's list, never inside
 * it.
 *
 * Every route below writes its own path out in full. Several of them appear on
 * other nouns word for word, and that repetition is deliberate: a reader asking
 * what `setLegalCompany` reaches should find the answer here rather than in a
 * factory that assembles it from a base path passed in from somewhere else.
 */
export function auctionHouseMethods({ sendRequest, buildUrl }) {
    return {
        ...collectionMethods({ basePath: BASE_PATH, sendRequest, buildUrl }),

        /**
         * One house by its four-character slug.
         *
         * The only route on this server that addresses a document by anything
         * other than its `_id`. The slug is the house's unique short name, which
         * is what a URL a person types or shares carries.
         */
        async readBySlug(slug) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/by-slug/${encodeURIComponent(slug)}`),
                'GET'
            );
        },

        /**
         * The Company this house legally is.
         *
         * @param {string|null} companyId - `null` clears it, which falls the
         *   resolution through to the Operation rather than leaving the house
         *   without a legal identity.
         */
        async setLegalCompany(id, companyId) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/legal-company`),
                'PUT',
                { companyId }
            );
        },

        /**
         * Who pays, and under which Stripe subscription.
         *
         * The server takes one of two bodies here and nothing between them:
         * `{ companyId, stripeSubscriptionId }` to name a payer, or
         * `{ companyId: null }` alone to clear. So a clear sends one key rather
         * than two, and a caller that passes a subscription with a null payer
         * gets the refusal it earned rather than a silently dropped field.
         *
         * @param {string|null} companyId
         * @param {string} [stripeSubscriptionId] - Required whenever a payer is named.
         */
        async setBillingCompany(id, companyId, stripeSubscriptionId) {
            const body = { companyId };
            if (stripeSubscriptionId !== undefined) {
                body.stripeSubscriptionId = stripeSubscriptionId;
            }
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/billing-company`),
                'PUT',
                body
            );
        },

        /**
         * The Operation this house belongs to.
         *
         * Not nullable, unlike the Company relationships: a house with no
         * Operation has nothing to resolve its legal identity or its payer
         * through, so the server refuses to leave one that way.
         */
        async setOperation(id, operationId) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/operation`),
                'PUT',
                { operationId }
            );
        },

        /**
         * Set one policy. The shape is the policy's own — the server stores it
         * as given and the application validates it against the key.
         */
        async setPolicy(id, policyName, policy) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/policies/${encodeURIComponent(policyName)}`),
                'PUT',
                policy
            );
        },

        /** Remove one policy. */
        async clearPolicy(id, policyName) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/policies/${encodeURIComponent(policyName)}`),
                'DELETE'
            );
        },

        /**
         * Replace this house's human knockout list whole.
         *
         * A replace rather than an add and a remove, because that is what the
         * route is: the server takes the list it is given and the list it is
         * given is the list. A caller adding one entry sends the others back.
         *
         * @param {Array<{ humanId: string, name: string }>} knockedOutHumans
         */
        async setKnockedOutHumans(id, knockedOutHumans) {
            return await sendRequest(
                buildUrl(`${BASE_PATH}/${encodeURIComponent(id)}/knocked-out-humans`),
                'PUT',
                { knockedOutHumans }
            );
        },

        /** Let a Dealership buy at this house. Addressed in the path, so no body. */
        async registerDealership(id, dealershipId) {
            return await sendRequest(
                buildUrl(
                    `${BASE_PATH}/${encodeURIComponent(id)}/registered-dealerships/${encodeURIComponent(dealershipId)}`
                ),
                'PUT'
            );
        },

        /** Take that registration away. */
        async unregisterDealership(id, dealershipId) {
            return await sendRequest(
                buildUrl(
                    `${BASE_PATH}/${encodeURIComponent(id)}/registered-dealerships/${encodeURIComponent(dealershipId)}`
                ),
                'DELETE'
            );
        },

        /**
         * Bar a registered Dealership from this house alone.
         *
         * House-tier knockout, which is a different thing from the Operation's:
         * a house may bar beyond its Operation's list and can never un-bar
         * something the Operation barred. Lifting this one lifts only what this
         * house set.
         */
        async knockOutRegisteredDealership(id, dealershipId) {
            return await sendRequest(
                buildUrl(
                    `${BASE_PATH}/${encodeURIComponent(id)}/registered-dealerships/${encodeURIComponent(dealershipId)}/knockout`
                ),
                'PUT'
            );
        },

        /** Lift this house's own bar. */
        async restoreRegisteredDealership(id, dealershipId) {
            return await sendRequest(
                buildUrl(
                    `${BASE_PATH}/${encodeURIComponent(id)}/registered-dealerships/${encodeURIComponent(dealershipId)}/knockout`
                ),
                'DELETE'
            );
        }
    };
}

export default auctionHouseMethods;
